import type { TError, TRemindReviewTeachersInput, TReviewReminderResult } from '@onesource/schemas';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import axios from '../../axios';
import type { TReviewMissingParams } from './use-review-missing';
import { getReviewQueryKey, toReviewSearchParams } from './use-review-queue';

interface TReminderInput<P> {
  /** The list's filters as the page holds them; paging is ignored. */
  filters: P;
  note?: string;
  /** Preview who would be reminded, without sending. */
  dryRun: boolean;
}

/**
 * Admin: remind teachers, one reminder per teacher and form, for the picked forms
 * (none = all) and review times (none = any). `targets` on send: the teacher + form
 * pairs left ticked.
 */
export const useRemindReviewTeachers = (spaceId: string) => {
  const queryClient = useQueryClient();
  return useMutation<TReviewReminderResult, TError, TRemindReviewTeachersInput>({
    mutationFn: async input => {
      const res = await axios.post<TReviewReminderResult>(
        `/community-hub/spaces/${spaceId}/review/reminders/teachers`,
        { ...input, note: input.note || undefined },
      );
      return res.data;
    },
    onSuccess: (_data, { dryRun }) => {
      if (!dryRun) {
        queryClient.invalidateQueries({ queryKey: getReviewQueryKey(spaceId) });
      }
    },
  });
};

/**
 * Teacher or admin: remind students of forms not submitted, one reminder per form —
 * all matching the filters, or only `studentUserUuids` (and only `formIds` when given).
 */
export const useRemindReviewStudents = (spaceId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    TReviewReminderResult,
    TError,
    TReminderInput<TReviewMissingParams> & {
      studentUserUuids?: string[];
      formIds?: string[];
      /** On send: the student + form pairs left ticked in the preview. */
      targets?: Array<{ studentUserUuid: string; formId: string }>;
    }
  >({
    mutationFn: async ({ filters, note, dryRun, studentUserUuids, formIds, targets }) => {
      const { page: _page, perPage: _perPage, ...rest } = filters;
      const res = await axios.post<TReviewReminderResult>(
        `/community-hub/spaces/${spaceId}/review/reminders/students`,
        {
          filters: toReviewSearchParams(rest),
          studentUserUuids,
          formIds,
          targets,
          note: note || undefined,
          dryRun,
        },
      );
      return res.data;
    },
    onSuccess: (_data, { dryRun }) => {
      if (!dryRun) {
        queryClient.invalidateQueries({ queryKey: getReviewQueryKey(spaceId) });
      }
    },
  });
};
